<script setup lang="ts">
/**
 * Full-row sticky date divider for grouped mobile tables (services,
 * payments). Selected by `DynamicTable.rowOverride` for synthetic
 * `kind: 'date-header'` rows spliced into the row list ahead of each date's
 * first item (mirrors `EventRowCell`'s pattern for the client card's
 * balance/disease rows).
 *
 * Renders one `<td colspan>` spanning every column: the date on the left,
 * the item count on the right (e.g. "3 sessions", "2 payments") —
 * `row.unitKey` is an i18n message key with pluralized forms (e.g.
 * `units.session`), resolved via `t(key, count)` so each locale's own
 * plural rules apply (English singular/plural; Russian one/few/many via
 * the custom `pluralRules.ru` in `@/i18n` — a plain English-style "+s"
 * would be wrong for Russian).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  row: { date: string; count: number; unitKey: string }
  colspan: number
}>()

const { t } = useI18n()

const countLabel = computed(() => t(props.row.unitKey, props.row.count))
</script>

<template>
  <td class="date-header-row font-tufte" :colspan="props.colspan">
    <div class="date-header-row__inner">
      <span class="date-header-row__date">{{ props.row.date }}</span>
      <span class="date-header-row__count">{{ countLabel }}</span>
    </div>
  </td>
</template>

<style scoped>
.date-header-row {
  /*
   * `position: sticky` targets the `<td>` directly (not the `<tr>`), same
   * rationale as DynamicTable's own pinned `<th>`: sticky on `<tr>`/`<thead>`
   * is unreliable under `border-collapse: collapse`. Callers hide their
   * `<thead>` entirely on mobile, so there's no competing sticky layer to
   * coordinate `top`/`z-index` against.
   *
   * The flex layout lives on an inner `<div>` rather than the `<td>` itself
   * — overriding a `<td>`'s display away from `table-cell` can break its
   * participation in table layout (colspan/column sizing).
   */
  position: sticky;
  top: 0;
  z-index: 1;
  background: color-mix(in srgb, var(--color-tufte-bg) 90%, var(--color-money-positive) 10%);
  /* Collapsed-border tables drop a plain `border-bottom` on sticky cells;
   * the inset shadow survives instead (mirrors DynamicTable's sticky `<th>`). */
  box-shadow: inset 0 -1px 0 var(--color-tufte-border);
  padding: 0.3rem 1.25rem !important;
  /*
   * `width: 1px` is the standard trick for a `colspan` cell in an
   * auto-layout table: without it, this cell's own content sets a floor on
   * how wide the browser makes the spanned columns, which can push the
   * whole table past the viewport edge on narrow screens. `1px` tells the
   * browser this cell contributes nothing to the width calculation — the
   * columns end up sized by the regular rows instead, and this cell just
   * fills whatever space that yields.
   */
  width: 1px;
}

.date-header-row__inner {
  display: flex;
  justify-content: space-between;
  padding-right: 1rem;
  align-items: baseline;
  /* Flex items default to `min-width: auto` (their content's natural
   * width), which would fight the `width: 1px` above. `min-width: 0` lets
   * the date/count text actually shrink to whatever room the table gives
   * this cell instead of forcing it back open. */
  min-width: 0;
}

.date-header-row__date {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.date-header-row__count {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: color-mix(in srgb, var(--color-tufte-muted) 60%, var(--color-tufte-bg));
}
</style>
